/*
 * <scroll-carousel> - lightweight scroll-snap carousel.
 * Markup:
 *   <scroll-carousel data-autoplay="true|false" data-speed="5" data-loop="true|false">
 *     <button data-carousel-prev>…</button>
 *     <ul data-carousel-track> <li data-carousel-slide>…</li> … </ul>
 *     <button data-carousel-next>…</button>
 *     <div data-carousel-dots></div>
 *   </scroll-carousel>
 */
if (!customElements.get('scroll-carousel')) {
  class ScrollCarousel extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.timer = null;
      this.paused = false;
      this.inView = true;
      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.dotsContainer = this.querySelector('[data-carousel-dots]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.loop = this.dataset.loop === 'true';

      if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        }
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.update);
      }

      this.setupAutoplay();
      this.handleEditorEvents();
      this.update();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      window.removeEventListener('resize', this.update);
    }

    get slides() {
      return Array.from(this.track.querySelectorAll('[data-carousel-slide]'));
    }

    get perView() {
      const slides = this.slides;
      if (!slides.length) return 1;
      const slideWidth = slides[0].getBoundingClientRect().width;
      if (!slideWidth) return 1;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return Math.max(1, Math.floor((this.track.clientWidth + gap + 1) / (slideWidth + gap)));
    }

    get pageCount() {
      return Math.max(1, this.slides.length - this.perView + 1);
    }

    update() {
      const pages = this.pageCount;
      this.classList.toggle('scroll-carousel--static', pages <= 1);
      this.buildDots(pages);
      this.onScroll();
    }

    buildDots(pages) {
      if (!this.dotsContainer) return;
      if (this.dotsContainer.childElementCount === pages) return;
      this.dotsContainer.innerHTML = '';
      if (pages <= 1) return;
      for (let i = 0; i < pages; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'scroll-carousel__dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1} of ${pages}`);
        dot.addEventListener('click', () => this.goTo(i));
        this.dotsContainer.appendChild(dot);
      }
    }

    currentIndex() {
      const slides = this.slides;
      if (!slides.length) return 0;
      const origin = slides[0].offsetLeft;
      const scrollLeft = Math.abs(this.track.scrollLeft);
      let closest = 0;
      let distance = Infinity;
      slides.forEach((slide, i) => {
        const d = Math.abs(slide.offsetLeft - origin - scrollLeft);
        if (d < distance) {
          distance = d;
          closest = i;
        }
      });
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (maxScroll > 0 && scrollLeft >= maxScroll - 2) closest = this.pageCount - 1;
      return Math.min(closest, this.pageCount - 1);
    }

    onScroll() {
      this.index = this.currentIndex();
      const pages = this.pageCount;
      if (this.dotsContainer) {
        Array.from(this.dotsContainer.children).forEach((dot, i) => {
          const active = i === this.index;
          dot.classList.toggle('is-active', active);
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }
      if (!this.loop) {
        if (this.prevButton) this.prevButton.disabled = this.index <= 0;
        if (this.nextButton) this.nextButton.disabled = this.index >= pages - 1;
      } else {
        if (this.prevButton) this.prevButton.disabled = pages <= 1;
        if (this.nextButton) this.nextButton.disabled = pages <= 1;
      }
    }

    goTo(index) {
      const slides = this.slides;
      const pages = this.pageCount;
      if (!slides.length) return;
      if (index < 0) index = this.loop ? pages - 1 : 0;
      if (index > pages - 1) index = this.loop ? 0 : pages - 1;
      const left = slides[index].offsetLeft - slides[0].offsetLeft;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollTo({
        left: rtl ? -left : left,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.index = index;
    }

    step(direction) {
      this.goTo(this.currentIndex() + direction);
    }

    setupAutoplay() {
      if (this.dataset.autoplay !== 'true') return;
      this.speed = Math.max(2, parseFloat(this.dataset.speed) || 5) * 1000;

      const pause = () => {
        this.paused = true;
      };
      const resume = () => {
        this.paused = false;
      };
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);
      this.track.addEventListener('touchstart', pause, { passive: true });
      this.track.addEventListener('touchend', () => setTimeout(resume, this.speed), { passive: true });

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting;
        });
        this.intersectionObserver.observe(this);
      }

      this.startAutoplay();
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.reducedMotion.matches) return;
      this.timer = setInterval(() => {
        if (this.paused || !this.inView || document.hidden) return;
        const pages = this.pageCount;
        if (pages <= 1) return;
        const next = this.currentIndex() + 1;
        if (next > pages - 1 && !this.loop) {
          this.goTo(0);
        } else {
          this.goTo(next);
        }
      }, this.speed);
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    handleEditorEvents() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      const section = this.closest('.shopify-section');
      if (!section) return;
      section.addEventListener('shopify:block:select', (event) => {
        this.paused = true;
        const i = this.slides.findIndex((slide) => slide.contains(event.target) || slide === event.target);
        if (i >= 0) this.goTo(Math.min(i, this.pageCount - 1));
      });
      section.addEventListener('shopify:block:deselect', () => {
        this.paused = false;
      });
    }
  }

  customElements.define('scroll-carousel', ScrollCarousel);
}
